import React, { useState } from 'react';
import { ExploreConfig } from '@explore-with-ai/core';
import './ConfigForm.css';

interface ConfigFormProps {
  config: ExploreConfig;
  onChange: (config: ExploreConfig) => void;
  onNext: () => void;
}

const ConfigForm: React.FC<ConfigFormProps> = ({ config, onChange, onNext }) => {
  const [fieldInput, setFieldInput] = useState({ name: '', description: '' });
  const [modeInput, setModeInput] = useState('');

  const handleInputChange = (field: keyof ExploreConfig, value: any) => {
    onChange({
      ...config,
      [field]: value
    });
  };

  const addField = () => {
    if (fieldInput.name.trim()) {
      const newFields = [...(config.fields || []), { ...fieldInput }];
      handleInputChange('fields', newFields);
      setFieldInput({ name: '', description: '' });
    }
  };

  const removeField = (index: number) => {
    const newFields = config.fields?.filter((_, i) => i !== index) || [];
    handleInputChange('fields', newFields);
  };

  const addMode = () => {
    if (modeInput.trim() && !config.modes?.includes(modeInput.trim())) {
      const newModes = [...(config.modes || []), modeInput.trim()];
      handleInputChange('modes', newModes);
      setModeInput('');
    }
  };

  const removeMode = (mode: string) => {
    const newModes = config.modes?.filter(m => m !== mode) || [];
    handleInputChange('modes', newModes);
  };

  const togglePlatform = (platform: string) => {
    const currentPlatforms = config.enabledPlatforms || [];
    const newPlatforms = currentPlatforms.includes(platform)
      ? currentPlatforms.filter(p => p !== platform)
      : [...currentPlatforms, platform];
    handleInputChange('enabledPlatforms', newPlatforms);
  };

  const availablePlatforms = ['chatgpt', 'claude', 'gemini', 'perplexity', 'grok'];

  const isValid = config.title && config.description && config.sourceName && config.sourceUrl;

  return (
    <div className="config-form">
      <div className="config-form__card">
        <h2 className="config-form__title">Configure Your Widget</h2>
        
        <div className="config-form__section">
          <h3 className="config-form__section-title">Basic Information</h3>
          
          <div className="config-form__field">
            <label className="config-form__label">Widget ID *</label>
            <input
              type="text"
              className="config-form__input"
              value={config.id}
              onChange={(e) => handleInputChange('id', e.target.value)}
              placeholder="unique-widget-id"
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Title *</label>
            <input
              type="text"
              className="config-form__input"
              value={config.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Market Analysis Dashboard"
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Description *</label>
            <textarea
              className="config-form__textarea"
              value={config.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what this dataset or visualization covers and what insights can be gained..."
              rows={4}
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Source Organization *</label>
            <input
              type="text"
              className="config-form__input"
              value={config.sourceName}
              onChange={(e) => handleInputChange('sourceName', e.target.value)}
              placeholder="e.g., Research Institute or Data Provider"
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Interactive Page URL *</label>
            <input
              type="url"
              className="config-form__input"
              value={config.sourceUrl}
              onChange={(e) => handleInputChange('sourceUrl', e.target.value)}
              placeholder="https://example.com/dashboard"
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Download URL (Optional)</label>
            <input
              type="url"
              className="config-form__input"
              value={config.downloadUrl || ''}
              onChange={(e) => handleInputChange('downloadUrl', e.target.value)}
              placeholder="https://example.com/data-download"
            />
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Format</label>
            <select
              className="config-form__select"
              value={config.format}
              onChange={(e) => handleInputChange('format', e.target.value)}
            >
              <option value="Interactive Visualization">Interactive Visualization</option>
              <option value="Interactive Dashboard">Interactive Dashboard</option>
              <option value="Research Report">Research Report</option>
              <option value="Data Explorer">Data Explorer</option>
              <option value="Statistical Analysis">Statistical Analysis</option>
            </select>
          </div>

          <div className="config-form__field">
            <label className="config-form__label">Last Updated</label>
            <input
              type="text"
              className="config-form__input"
              value={config.lastUpdated || ''}
              onChange={(e) => handleInputChange('lastUpdated', e.target.value)}
              placeholder="e.g., Updated monthly, Last updated: March 2024"
            />
          </div>
        </div>

        <div className="config-form__section">
          <h3 className="config-form__section-title">Data Fields</h3>
          <p className="config-form__section-description">
            Define the key fields in your dataset to help AI understand the data structure.
          </p>
          
          <div className="config-form__field-builder">
            <div className="config-form__field-inputs">
              <input
                type="text"
                className="config-form__input"
                placeholder="Field name"
                value={fieldInput.name}
                onChange={(e) => setFieldInput({ ...fieldInput, name: e.target.value })}
              />
              <input
                type="text"
                className="config-form__input"
                placeholder="Description (optional)"
                value={fieldInput.description}
                onChange={(e) => setFieldInput({ ...fieldInput, description: e.target.value })}
              />
              <button
                type="button"
                onClick={addField}
                className="config-form__add-button"
              >
                Add Field
              </button>
            </div>
          </div>

          {config.fields && config.fields.length > 0 && (
            <div className="config-form__field-list">
              {config.fields.map((field, index) => (
                <div key={index} className="config-form__field-item">
                  <div className="config-form__field-info">
                    <strong>{field.name}</strong>
                    {field.description && <span> - {field.description}</span>}
                  </div>
                  <button
                    onClick={() => removeField(index)}
                    className="config-form__remove-button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="config-form__section">
          <h3 className="config-form__section-title">Analysis Modes</h3>
          <p className="config-form__section-description">
            Customize the analysis modes available for this dataset.
          </p>
          
          <div className="config-form__mode-builder">
            <input
              type="text"
              className="config-form__input"
              placeholder="Custom analysis mode"
              value={modeInput}
              onChange={(e) => setModeInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addMode()}
            />
            <button
              type="button"
              onClick={addMode}
              className="config-form__add-button"
            >
              Add Mode
            </button>
          </div>

          {config.modes && config.modes.length > 0 && (
            <div className="config-form__mode-list">
              {config.modes.map((mode) => (
                <div key={mode} className="config-form__mode-item">
                  <span>{mode}</span>
                  <button
                    onClick={() => removeMode(mode)}
                    className="config-form__remove-button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="config-form__section">
          <h3 className="config-form__section-title">AI Platforms</h3>
          <p className="config-form__section-description">
            Select which AI platforms should be available for this widget.
          </p>
          
          <div className="config-form__platform-list">
            {availablePlatforms.map((platform) => (
              <label key={platform} className="config-form__checkbox-label">
                <input
                  type="checkbox"
                  checked={config.enabledPlatforms?.includes(platform) || false}
                  onChange={() => togglePlatform(platform)}
                  className="config-form__checkbox"
                />
                <span className="config-form__checkbox-text">
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-form__section">
          <h3 className="config-form__section-title">Target Audience</h3>
          <p className="config-form__section-description">
            Select the primary audience for AI analysis to customize prompt tone and focus.
          </p>
          
          <div className="config-form__audience-grid">
            {[
              { value: 'general', label: 'General Public', description: 'Clear explanations for broad audiences' },
              { value: 'journalist', label: 'Journalists', description: 'Focus on narrative and newsworthy angles' },
              { value: 'researcher', label: 'Researchers', description: 'Technical details and methodology focus' },
              { value: 'policy', label: 'Policy Makers', description: 'Implications and actionable insights' }
            ].map((audience) => (
              <label key={audience.value} className="config-form__audience-option">
                <input
                  type="radio"
                  name="audience"
                  value={audience.value}
                  checked={config.audience === audience.value || (!config.audience && audience.value === 'general')}
                  onChange={(e) => handleInputChange('audience', e.target.value)}
                  className="config-form__radio"
                />
                <div className="config-form__audience-content">
                  <h4>{audience.label}</h4>
                  <p>{audience.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="config-form__section">
          <h3 className="config-form__section-title">Button Style</h3>
          <p className="config-form__section-description">
            Choose the visual style for your Explore with AI button.
          </p>
          
          <div className="config-form__button-style-grid">
            {[
              { value: 'default', label: 'Default', description: 'Classic blue button with shadow' },
              { value: 'minimal', label: 'Minimal', description: 'Clean light button with border' },
              { value: 'gradient', label: 'Gradient', description: 'Purple gradient with glow effect' },
              { value: 'outline', label: 'Outline', description: 'Transparent with colored border' }
            ].map((style) => (
              <label key={style.value} className="config-form__button-style-option">
                <input
                  type="radio"
                  name="buttonStyle"
                  value={style.value}
                  checked={config.buttonStyle === style.value || (!config.buttonStyle && style.value === 'default')}
                  onChange={(e) => handleInputChange('buttonStyle', e.target.value)}
                  className="config-form__radio"
                />
                <div className="config-form__button-style-content">
                  <div className={`config-form__button-preview config-form__button-preview--${style.value}`}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Explore with AI
                  </div>
                  <div className="config-form__button-style-info">
                    <h4>{style.label}</h4>
                    <p>{style.description}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="config-form__actions">
          <button
            onClick={onNext}
            disabled={!isValid}
            className="config-form__next-button"
          >
            Preview Widget
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigForm;
